import { getShuttleStopOptions } from "../../Lib/Utils/shuttleStops";
import { PtModeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking, ILegTrain, ISelectedTrainLegs } from "../../Orchestrator/Types/Index";
import { sortE2ELegsByStartTime } from "../../Lib/Utils/buildE2ELegs";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleAddedJourney, IShuttleAddTarget } from "../../Orchestrator/Types/Interface/IShuttleBus";
import { getAllOfferWithoutExchange, getAllTripWithoutExchange } from "../../Lib/Utils/booking";
import { bookedShuttleListsForLeg, isShuttleFromAnotherLegDate } from "../OnwardTravel/bookedShuttlesForLeg";
import { uncoveredShuttleRefs } from "../OnwardTravel/shuttleCoverage";
import { shuttleOfferPassengers, stopsWithTrips } from "./shuttleTripsCache";
import { hasPendingExchange, OWNED_ADMISSION_STATUSES } from "../OnwardTravel/hasPendingExchange";
import { hasAnyLegDeparted } from "../../Lib/Utils/booking";
import { bookingAppearanceMinutes, bookingsPastPivot } from "../../Lib/Utils/bookingAppearancePeriod";

/**
 * The order ManageEndToEndRow renders a leg's two sides in. Used only to keep
 * the enumerated targets in the order the passenger sees them.
 */
const SHUTTLE_SIDE_ORDER: RideHailingMode[] = ["toStation", "fromStation"];

export interface IEligibleTargetsInput {
    booking: IBooking | undefined;
    /** Train legs in the order they are rendered — outbound before inbound. */
    legs: ILegTrain[];
    /** stopsResponseAtom: shuttle sub-stations hang off these stations. */
    allStops: any[];
    /** shuttleAddedAtom — an add made this session whose booking refresh lagged. */
    shuttleAdded: Partial<Record<RideHailingMode, IShuttleAddedJourney>>;
    /** refundedShuttleOfferIdsAtom — refunded here, not yet terminal on the booking. */
    refundedOfferIds: string[];
    isShuttleEnabled: boolean;
    isActiveBooking: boolean;
    /**
     * shuttleStopAvailabilityAtom — stops the sweep found with no departures.
     * The row only draws stops that pass stopsWithTrips, so a side whose every
     * stop is known-empty (late in the appearance period, say) has no row; the
     * menu must not offer it either. Omitted = unknown = every stop kept.
     */
    stopAvailability?: Record<string, boolean>;
}

/**
 * The one train leg per direction that a ManageEndToEndRow actually renders —
 * sorted, then the first train leg, exactly as the row picks it. Everything that
 * reasons about shuttle eligibility (the Manage-booking menu gate, the request
 * coordinator) must count the same legs the row will offer, or the three drift.
 */
export function primaryTrainLegs(tripDetails: ISelectedTrainLegs | undefined): ILegTrain[] {
    return [tripDetails?.outbound, tripDetails?.inbound]
        .map((directionLegs) => sortE2ELegsByStartTime(directionLegs || []).find((leg) => leg?.trainType === "train"))
        .filter((leg): leg is ILegTrain => !!leg);
}

const stationRefForSide = (leg: ILegTrain, side: RideHailingMode): string =>
    (side === "toStation" ? leg.origin?.stopPlaceRef : leg.destination?.stopPlaceRef) || "";

/**
 * Can this side of this leg still take a shuttle, by the clock alone?
 *
 * Before departure, both sides. After it, only the ARRIVAL side, and only while
 * the booking-ticket appearance period keeps the ticket under Upcoming: a
 * passenger on the train can still book the bus from the station they are
 * heading to, but a shuttle TO the departure station would have to have run
 * before the train did. Same pivot as the Upcoming tab, so the shuttle add
 * disappears exactly when the ticket moves to Past, not before or after.
 *
 * An unreadable start time leaves the side open, as hasAnyLegDeparted does.
 */
export function isShuttleSideOpen(
    leg: ILegTrain | undefined | null,
    side: RideHailingMode,
    now: number = Date.now()
): boolean {
    if (!hasAnyLegDeparted([leg?.startTime], now)) return true;
    return side === "fromStation" && !hasAnyLegDeparted([leg?.startTime], bookingsPastPivot(new Date(now)).getTime());
}

/**
 * The instant isShuttleSideOpen turns false for this side: the departure for
 * toStation, the departure plus the appearance period for fromStation.
 * Infinity for an unreadable start time — open, as isShuttleSideOpen leaves it.
 */
export function shuttleSideClosesAt(leg: ILegTrain | undefined | null, side: RideHailingMode): number {
    const departure = new Date(leg?.startTime ?? "").getTime();
    if (!Number.isFinite(departure)) return Infinity;
    return side === "toStation" ? departure : departure + bookingAppearanceMinutes() * 60_000;
}

/**
 * The instants isShuttleSideOpen changes its answer for these legs: each
 * departure, and each departure plus the appearance period. For useRerenderAt,
 * so a page left open drops the shuttle add on time.
 */
export function shuttleClockBoundaries(legs: (ILegTrain | undefined | null)[]): number[] {
    const periodMs = bookingAppearanceMinutes() * 60_000;
    return legs
        .map((leg) => new Date(leg?.startTime ?? "").getTime())
        .filter((time) => Number.isFinite(time))
        .flatMap((time) => [time, time + periodMs]);
}

/**
 * Every place this booking could still take a shuttle, in the order the
 * passenger sees them.
 *
 * Two consumers, one rule: the Manage-booking menu shows its item when this is
 * non-empty, and a ManageEndToEndRow expands the sides it names. Mirrors the
 * row's own eligibility checks — same stop lookup, same booking-derived
 * ownership, same pending-exchange block — so the menu never offers what the
 * section then refuses to show.
 */
export function eligibleShuttleTargets(input: IEligibleTargetsInput): IShuttleAddTarget[] {
    const { booking, legs, allStops, shuttleAdded, refundedOfferIds, isShuttleEnabled, isActiveBooking, stopAvailability } =
        input;
    if (!isShuttleEnabled || !isActiveBooking || !booking) return [];
    // Turnit allows one uncommitted modification per booking: adding an offer
    // while an exchange awaits payment discards that exchange server-side.
    if (hasPendingExchange(booking)) return [];

    const ownedOfferIds = new Set(
        (booking.bookedOffers || [])
            .filter((offer) =>
                ((offer as { admissions?: { status?: string }[] })?.admissions || []).some((admission) =>
                    OWNED_ADMISSION_STATUSES.has(admission?.status || "")
                )
            )
            .map((offer) => (offer as { offerId?: string })?.offerId)
    );

    // Legs the booking does not own are a previous booking's, still in the
    // global atom — count nothing rather than counting the wrong thing.
    //
    // Departure is judged per SIDE below (isShuttleSideOpen), and for the same
    // reason the ownership filter is here rather than at the call sites: every
    // consumer — the menu item, the list row, the section's expanded sides —
    // reads this one answer, so a guard at one call site and not another is
    // exactly the dead click this function exists to remove.
    const ownLegs = (legs || []).filter((leg) => bookingOwnsLeg(booking, leg?.legId));
    if (ownLegs.length === 0) return [];

    const targets: IShuttleAddTarget[] = [];
    ownLegs.forEach((leg) => {
        if (!leg?.legId) return;
        // ownedOnly: the ownership check below happens AFTER the side has been
        // collapsed to one offer, so an unpaid offer reaching that point wins
        // the side and is then discarded — reopening a side that already holds
        // a paid shuttle. Exclusion has to happen before the collapse.
        const bookedSides = bookedShuttleListsForLeg(booking, leg, refundedOfferIds, {
            ownedOnly: true,
        });
        SHUTTLE_SIDE_ORDER.forEach((side) => {
            if (!isShuttleSideOpen(leg, side)) return;
            const ref = stationRefForSide(leg, side);
            const stopOptions = getShuttleStopOptions(allStops || [], ref);
            if (stopOptions.length === 0) return;
            // A target is a place to BUY, so it needs a stop the row would draw
            // (its stopsWithTrips filter). The ownership filters below still use
            // the UNFILTERED list, as the row's resolveShuttleEntries does.
            if (stopsWithTrips(stopOptions, stopAvailability, side, leg).length === 0) return;
            // Same two filters the row applies: the entry must be one this
            // booking owns AND belong to this side, or it is another
            // direction's shuttle leaking through the side-keyed atom.
            const fromBooking = bookedSides[side] || [];
            const candidates = fromBooking.length > 0
                ? fromBooking
                : [shuttleAdded?.[side]].filter((c): c is IShuttleAddedJourney => !!c);
            const stopIds = new Set(stopOptions.map((option) => option?.stopId));
            const owned = candidates.filter(
                (candidate) =>
                    ownedOfferIds.has(candidate.offerId) &&
                    stopIds.has(candidate.stopId) &&
                    // Third filter, added with the row's: on a booking split across
                    // two same-route trips by a partial change-date, the atom hands
                    // this leg the SIBLING's shuttle. Counted as already-added it
                    // removes the target, so the menu item disappears while the row
                    // still offers the purchasable side — the exact drift the
                    // docstring above promises not to have.
                    !isShuttleFromAnotherLegDate(booking, leg, candidate, side)
            );
            // COVERAGE, not existence. A side holding a shuttle for two of three
            // riders is still a place this booking can take one, and gating on
            // "has any shuttle" is what made the third passenger unsellable from
            // both this menu and the row. Empty uncovered set = fully covered, or
            // riders we cannot read — [[uncoveredShuttleRefs]] refuses on the
            // latter rather than offering to double-sell.
            //
            // Judged on what the BOOKING holds, not on `owned`: the filters above
            // drop an entry whose stop is missing from the stops dataset, which is
            // right for targeting and backwards for coverage — a fully-sold side
            // whose offer got dropped would read as "nothing owned here" and be
            // offered again. The row applies the same length check; the two must
            // agree or the menu offers what the section then refuses.
            const candidatesFromBooking = bookedSides[side] || [];
            if (candidatesFromBooking.length > 0 && candidatesFromBooking.length !== owned.length) {
                return;
            }
            if (owned.length > 0) {
                // null = refuse (riders unreadable), [] = fully covered. Both
                // close the side; only the reason differs.
                const uncovered = uncoveredShuttleRefs(shuttleOfferPassengers(booking.passengers), owned);
                if (uncovered === null || uncovered.length === 0) return;
            }
            targets.push({ legId: leg.legId, side });
        });
    });
    return targets;
}

/**
 * The booking's TRAIN legs, in the shape the eligibility rule reads, straight
 * from a raw booking response.
 *
 * The bookings LIST has no `selectedTrainLegsAtom` for a row — that atom holds
 * whichever booking was opened last — so the list derives its legs here instead.
 * Shuttle legs are excluded: they are the product being added, not a place to
 * add one to.
 */
export function trainLegsFromBooking(booking: IBooking | undefined): ILegTrain[] {
    if (!booking) return [];
    // Exchanged-away trips still carry their legs on the booking, and
    // hasPendingExchange only screens exchanges still in flight — counting them
    // would offer a shuttle on a trip the passenger no longer holds.
    const liveTrips = getAllTripWithoutExchange(
        getAllOfferWithoutExchange(booking.bookedOffers || []),
        (booking.trips || []) as any
    );
    // ONE train leg per trip, mirroring primaryTrainLegs on the details side: a
    // non-direct trip has several, and counting each would make the list offer
    // more places than the section then shows.
    return (liveTrips || [])
        .map((trip: any) =>
            (trip?.legs || []).find((leg: any) => leg?.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN)
        )
        .filter(Boolean)
        .map((leg: any) => ({
            legId: leg?.id,
            origin: { stopPlaceRef: leg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "" },
            destination: { stopPlaceRef: leg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "" },
            // Same two fields GetLegsFromTripAction reads for a details-page
            // leg. Refs alone cannot tell two same-route trips apart, so
            // without the times isShuttleFromAnotherLegDate answers "unknown"
            // for every leg built here and the eligibility rule below silently
            // stops matching what the details row does.
            startTime: leg?.timedLeg?.start?.serviceDeparture?.timetabledTime || "",
            endTime: leg?.timedLeg?.end?.serviceArrival?.timetabledTime || "",
        }) as unknown as ILegTrain);
}

/**
 * Does this booking actually contain this leg?
 *
 * selectedTrainLegsAtom is a global that no page resets, and the booking-details
 * page writes bookingResponseAtom BEFORE the summary rebuilds the legs — so
 * there is a window where the booking is B and the legs are still A's. Comparing
 * ids is the only thing that catches it: A's legs are simply absent from B's
 * trips. Without this the eligibility count is computed against another
 * booking's legs and, because the request resolves once, latched forever.
 */
export function bookingOwnsLeg(booking: IBooking | undefined, legId: string | undefined): boolean {
    if (!booking || !legId) return false;
    return (booking.trips || []).some((trip: any) =>
        (trip?.legs || []).some((leg: any) => leg?.id === legId)
    );
}
